<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { fetchOpenApiToken, issueOpenApiToken } from './api';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const getters = useStoreGetters();
const accountId = computed(() => getters.getCurrentAccountId.value);

const loading = ref(true);
const issuing = ref(false);
const current = ref(null);
const history = ref([]);
const plaintext = ref('');
const showResetConfirm = ref(false);

const load = async () => {
  loading.value = true;
  try {
    const payload = await fetchOpenApiToken(accountId.value);
    current.value = payload.current;
    history.value = payload.history || [];
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.OPEN_API.ERROR_LOAD'));
  } finally {
    loading.value = false;
  }
};

const issue = async () => {
  issuing.value = true;
  showResetConfirm.value = false;
  try {
    const payload = await issueOpenApiToken(accountId.value);
    current.value = payload.current;
    history.value = payload.history || [];
    plaintext.value = payload.token || '';
    useAlert(t('INTEGRATION_SETTINGS.OPEN_API.ISSUED'));
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.OPEN_API.ERROR_ISSUE'));
  } finally {
    issuing.value = false;
  }
};

const onPrimaryClick = () => {
  if (current.value) {
    showResetConfirm.value = true;
    return;
  }
  issue();
};

const copyToken = async () => {
  await copyTextToClipboard(plaintext.value);
  useAlert(t('INTEGRATION_SETTINGS.OPEN_API.COPIED'));
};

onMounted(load);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('INTEGRATION_SETTINGS.OPEN_API.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.OPEN_API.TITLE')"
        :description="$t('INTEGRATION_SETTINGS.OPEN_API.DESCRIPTION')"
        feature-name="integrations"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      >
        <template #actions>
          <NextButton
            size="sm"
            :is-loading="issuing"
            :label="
              current
                ? $t('INTEGRATION_SETTINGS.OPEN_API.RESET')
                : $t('INTEGRATION_SETTINGS.OPEN_API.GENERATE')
            "
            @click="onPrimaryClick"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="flex flex-col gap-6 max-w-3xl">
        <div
          class="p-4 outline outline-n-container outline-1 bg-n-card rounded-xl"
        >
          <h3 class="text-heading-3 text-n-slate-12 mb-2">
            {{ $t('INTEGRATION_SETTINGS.OPEN_API.CURRENT') }}
          </h3>
          <p v-if="!current" class="text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.OPEN_API.EMPTY') }}
          </p>
          <template v-else>
            <p class="font-mono text-sm mb-2">{{ current.masked }}</p>
            <p class="text-sm text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.OPEN_API.CREATED_AT') }}:
              {{ current.created_at }}
              <span v-if="current.created_by">
                · {{ current.created_by }}
              </span>
            </p>
          </template>
          <div v-if="plaintext" class="mt-4">
            <p class="text-sm text-n-slate-11 mb-2">
              {{ $t('INTEGRATION_SETTINGS.OPEN_API.PLAINTEXT_HINT') }}
            </p>
            <div class="flex items-center gap-2">
              <input
                :value="plaintext"
                type="text"
                readonly
                class="!mb-0 font-mono"
              />
              <NextButton
                type="button"
                icon="i-lucide-copy"
                slate
                faded
                @click="copyToken"
              />
            </div>
          </div>
        </div>

        <div
          v-if="history.length"
          class="p-4 outline outline-n-container outline-1 bg-n-card rounded-xl"
        >
          <h3 class="text-heading-3 text-n-slate-12 mb-3">
            {{ $t('INTEGRATION_SETTINGS.OPEN_API.HISTORY') }}
          </h3>
          <div
            v-for="row in history"
            :key="row.id"
            class="text-sm text-n-slate-11 py-2 border-b border-n-weak last:border-0"
          >
            <span class="font-mono">{{ row.masked }}</span>
            · {{ $t('INTEGRATION_SETTINGS.OPEN_API.REVOKED_AT') }}:
            {{ row.revoked_at }}
            <span v-if="row.revoked_by"> · {{ row.revoked_by }}</span>
          </div>
        </div>
      </div>

      <woot-delete-modal
        v-model:show="showResetConfirm"
        :on-close="() => (showResetConfirm = false)"
        :on-confirm="issue"
        :title="$t('INTEGRATION_SETTINGS.OPEN_API.RESET_TITLE')"
        :message="$t('INTEGRATION_SETTINGS.OPEN_API.RESET_MESSAGE')"
        :confirm-text="$t('INTEGRATION_SETTINGS.OPEN_API.RESET')"
        :reject-text="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.CANCEL')"
      />
    </template>
  </SettingsLayout>
</template>
